import React, { useState, useEffect, useRef } from "react";
import { MdDelete, MdEdit, MdAdd, MdFileDownload, MdSort } from "react-icons/md";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { Spinner as SpinnerBoot } from "react-bootstrap";
import Select from "react-select";
import { formatDate, formatTime, today } from "../../../Helper/Helper";
import { useSelector } from "react-redux";
import { SendPostRequest } from "../../../http";
import Spinner from "../../Spinner";
import * as echarts from "echarts";
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Custom CSS for styling
const customStyles = `
  .blood-glucose-container {
    padding: 20px;
  }
  
  .chart-views-btn, .chart-views-btn-second {
    background-color: #f1f1f1;
    border: 1px solid #ddd;
    padding: 8px 16px;
    cursor: pointer;
    transition: all 0.3s;
  }
  
  .chart-views-btn {
    border-radius: 5px 0 0 5px;
  }
  
  .chart-views-btn-second {
    border-radius: 0 5px 5px 0;
    border-left: none;
  }
  
  .chart-active-views-btn {
    background-color: #000 !important;
    color: white !important;
    border-color: #000 !important;
  }
  
  .measurement-type-select, .time-range-select {
    min-width: 150px;
  }
  
  .sort-dropdown {
    min-width: 200px;
  }
  
  .table thead th {
    background-color: #f2f2f2;
    font-weight: 600;
  }
  
  .table-hover tbody tr:hover {
    background-color: rgba(0, 0, 0, 0.075);
  }
  
  .table tbody td {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  
  .input-group-unit {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  
  .input-group-unit .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  
  .input-group-unit .input-group-append {
    margin-left: -1px;
  }
  
  .input-group-unit .input-group-text {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  
  .action-buttons {
    padding: 0.25rem 0.5rem;
  }
`;

// Blood Glucose Chart Component
const BloodGlucoseChart = ({ data, loading, unit, measurementType }) => {
  const chartRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Helper to format x-axis date labels as "12 Mar 2025" and wrap text
  const formatAxisDateLabel = (value) => {
    if (!value) return value;

    try {
      let year, month, day;

      // Expecting YYYY-MM-DD or a parsable date string
      if (typeof value === "string" && value.includes("-")) {
        const parts = value.split("-");
        if (parts.length >= 3) {
          year = parts[0];
          month = parts[1];
          day = parts[2].substring(0, 2); // strip any time part if present
        }
      }

      // Fallback: parse with Date
      if (!year || !month || !day) {
        const d = new Date(value);
        if (!isNaN(d.getTime())) {
          year = d.getFullYear().toString();
          month = (d.getMonth() + 1).toString().padStart(2, "0");
          day = d.getDate().toString().padStart(2, "0");
        }
      }

      if (!year || !month || !day) return value;

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthIndex = parseInt(month, 10) - 1;
      const monthName = monthNames[monthIndex] || month;

      // Wrap into two lines: "12 Mar\n2025"
      return `${parseInt(day, 10)} ${monthName}\n${year}`;
    } catch (e) {
      return value;
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (
      !data ||
      !data.label ||
      !Array.isArray(data.label) ||
      data.label.length === 0
    ) {
      return;
    }

    // Calculate min and max values
    const validValues = data.value.filter(
      (val) => val !== null && val !== undefined
    );
    if (validValues.length === 0) return;

    const minValue = Math.min(...validValues);
    const maxValue = Math.max(...validValues);

    // Build series data
    const seriesData = data.value.map((value, index) => ({
      value,
      itemStyle: {
        color: data.color?.[index] || "#4F81BC",
      },
    }));

    const option = {
      title: {
        text: `Blood Glucose Trends (${measurementType})`,
        left: "center",
        textStyle: { fontSize: 16, fontWeight: "bold" },
      },
      tooltip: {
        trigger: "axis",
        // Keep tooltip within chart area to avoid going off-screen
        confine: true,
        formatter: function (params) {
          const glucose = params[0];
          const date = glucose.axisValue;
          const value = glucose.data.value || glucose.data;
          const index = glucose.dataIndex;
          let status = data.status?.[index] ? ` (${data.status[index]})` : "";
          return `${date}<br/>${glucose.marker} ${glucose.seriesName}: ${value} ${unit}${status}`;
        },
      },
      legend: {
        data: ["Glucose"],
        bottom: 10,
      },
      grid: {
        left: isMobile ? "3%" : "3%",
        right: isMobile ? "15%" : "8%",
        bottom: isMobile ? "2%" : "15%",
        containLabel: true,
      },
      xAxis: {
        show: !isMobile,
        type: "category",
        data: data.label,
        axisLabel: {
          // Dynamically skip some labels when there are too many to avoid overlap
          interval: (index) => {
            const len = (data.label || []).length || 0;
            if (len > 20) return index % 4 === 0; // show every 4th label
            if (len > 12) return index % 3 === 0; // show every 3rd label
            if (len > 8) return index % 2 === 0; // show every 2nd label
            return true; // show all when few
          },
          // Use custom formatter to change date format and wrap text
          formatter: (value) => formatAxisDateLabel(value),
          // Let ECharts hide overlapping labels as a fallback
          hideOverlap: true,
          margin: 12,
          rotate: 0,
          fontSize: 10,
        },
        axisLine: { show: true },
        axisTick: { show: true },
      },
      yAxis: {
        type: "value",
        name: unit,
        nameLocation: "end",
        nameTextStyle: {
          padding: [0, 0, 0, 16],
        },
        min: Math.max(0, minValue - 20),
        max: maxValue + 20,
        splitLine: { show: true, lineStyle: { type: "dashed" } },
        axisLabel: {
          fontSize: 12,
          align: "end",
          margin: 30,
          hideOverlap: true,
        },
      },
      visualMap: {
        show: false,
        dimension: 0,
        pieces: data.color?.map((clr, idx) => ({
          gt: idx - 1,
          lte: idx,
          color: clr,
        })),
      },
      series: [
        {
          name: "Glucose",
          type: "line",
          data: seriesData,
          smooth: true,
          symbol: "circle",
          // Match weight chart behavior: smaller symbols and down-sampling
          symbolSize: 5,
          showAllSymbol: "auto",
          sampling: "lttb",
          lineStyle: { width: 3 },
          encode: { x: 0, y: 1 },
          // Highlight maximum and minimum glucose values
          markPoint: {
            data: [
              { type: "max", name: "Max" },
              { type: "min", name: "Min" },
            ],
          },
          markLine: {
            data: [{ type: "average", name: "Avg" }],
            lineStyle: { type: "dashed" },
            label: {
              show: true,
              position: "end",
              formatter: ({ value }) => value.toFixed(1),
            },
          },
        },
      ],
    };

    const chart = echarts.init(chartRef.current);
    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [data, unit, measurementType]);

  return loading ? (
    <div className="text-center py-5">
      <Spinner />
      <p className="mt-2">Loading chart data...</p>
    </div>
  ) : (
    <>
      <div className="pt-4 text-center fw-bold">
        {!data || !data.label || data.label.length === 0
          ? "No Record Available"
          : ""}
      </div>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height:
            data && data.label && data.label.length > 0 ? "400px" : "auto",
        }}
      />
    </>
  );
};

// Main Blood Glucose Component
function BloodGlucose() {
  const patientData = useSelector((store) => store.patient.patientLogin);
  const [viewMode, setViewMode] = useState("chart");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [LoadingEdit, setLoadingEdit] = useState(false);
  const [LoadingDelete, setLoadingDelete] = useState(false);
  const [filterData, setFilterData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [getData, setGetData] = useState([]);
  const [graphData, setGraphData] = useState({ label: [], value: [] });
  const [editData, setEditData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [addData, setAddData] = useState({
    measurement: "",
    measurement_type: "Fasting",
    record_date: "",
    record_time: "",
    notes: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedTimeStamp, setSelectedTimeStamp] = useState({ value: "4-week", label: "4 Weeks" });
  const [selectedMeasurementType, setSelectedMeasurementType] = useState({ value: "All", label: "All" });
  const [glucoseUnit, setGlucoseUnit] = useState("mg/dL");
  const [inputUnit, setInputUnit] = useState("mg/dL");
  const [sortOption, setSortOption] = useState({ value: 'record_date-desc', label: 'Date (Newest First)' });

  const timeStamp = [
    { value: "1-week", label: "1 Week" },
    { value: "2-week", label: "2 Weeks" },
    { value: "3-week", label: "3 Weeks" },
    { value: "4-week", label: "4 Weeks" },
  ];

  const measurementTypeOptions = [
    { value: "All", label: "All" },
    { value: "Fasting", label: "Fasting" },
    { value: "After Meal", label: "After Meal" },
    { value: "Random", label: "Random" },
  ];

  const unitOptions = [
    { value: "mg/dL", label: "mg/dL" },
    { value: "mmol/L", label: "mmol/L" },
  ];

  const sortOptions = [
    { value: 'record_date-desc', label: 'Date (Newest First)' },
    { value: 'record_date-asc', label: 'Date (Oldest First)' },
    { value: 'measurement-desc', label: 'Glucose Level (High to Low)' },
    { value: 'measurement-asc', label: 'Glucose Level (Low to High)' },
  ];

  // Add custom CSS for styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = customStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
    fetchGraphData();
  }, []);

  useEffect(() => {
    fetchData();
    fetchGraphData();
  }, [selectedMeasurementType, glucoseUnit]);

  useEffect(() => {
    fetchGraphData();
  }, [selectedTimeStamp]);

  useEffect(() => {
    handleSortChange(sortOption);
  }, [sortOption]);

  const fetchData = async (filter) => {
    setIsLoading(true);
    try {
      const response = await SendPostRequest("healthcare/blood_glucose/grid", {
        logged_id: patientData?.logged_id,
        filter: filter || {},
        measurement_type: selectedMeasurementType.value === "All" ? "" : selectedMeasurementType.value,
        unit: glucoseUnit
      });
      if (response?.status === "success") {
        const data = response?.data || [];
        setGetData(data);
        handleSortChange(sortOption, data);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGraphData = async () => {
    setLoadingGraph(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/graph",
        {
          logged_id: patientData?.logged_id,
          timespan: selectedTimeStamp?.value || "4-week",
          measurement_type: selectedMeasurementType.value === "All" ? "" : selectedMeasurementType.value,
          unit: glucoseUnit
        }
      );
      if (response?.status === "success") {
        setGraphData(response?.data || { label: [], value: [] });
      } else {
        toast.error(response?.message);
        setGraphData({ label: [], value: [] });
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to fetch graph data");
      setGraphData({ label: [], value: [] });
    } finally {
      setLoadingGraph(false);
    }
  };

  const handleSortChange = (option, dataToSort = getData) => {
    const [key, direction] = option.value.split('-');
    const sortedData = [...dataToSort].sort((a, b) => {
      if (key === 'record_date') {
        const dateA = new Date(a[key]);
        const dateB = new Date(b[key]);
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (key === 'measurement') {
        const valueA = parseFloat(a[key]);
        const valueB = parseFloat(b[key]);
        return direction === 'asc' ? valueA - valueB : valueB - valueA;
      } else {
        if (a[key] < b[key]) {
          return direction === 'asc' ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });

    setGetData(sortedData);
    setSortOption(option);
  };

  const handleModalOpen = () => {
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData({});
    setValidationErrors({});
    setAddData({
      measurement: "",
      measurement_type: "Fasting",
      record_date: "",
      record_time: "",
      notes: "",
    });
    setInputUnit("mg/dL");
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    // Prevent negative values for measurement field
    if (name === 'measurement' && value.includes('-')) {
      return;
    }

    // Validate inputs
    let errors = { ...validationErrors };

    if (name === 'measurement') {
      // Handle different validation based on input unit
      if (inputUnit === 'mg/dL') {
        // For mg/dL: only integers, no decimals
        if (!/^\d*$/.test(value)) {
          errors[name] = 'Please enter a whole number for mg/dL';
        } else {
          const numValue = parseInt(value);
          if (numValue < 20) {
            errors[name] = 'Glucose must be at least 20 mg/dL';
          } else if (numValue > 2500) {
            errors[name] = 'Glucose must be less than 2500 mg/dL';
          } else {
            delete errors[name];
          }
        }
      } else {
        // For mmol/L: allow one decimal place
        if (!/^\d*(\.\d{0,1})?$/.test(value)) {
          errors[name] = 'You can only enter up to one decimal place';
        } else {
          const numValue = parseFloat(value);
          if (numValue < 1) {
            errors[name] = 'Glucose must be at least 1 mmol/L';
          } else if (numValue > 150) {
            errors[name] = 'Glucose must be less than 150 mmol/L';
          } else {
            delete errors[name];
          }
        }
      }
    }

    if (name === 'record_time') {
      const currentTime = new Date().toTimeString().slice(0, 5);
      if (addData.record_date === new Date().toISOString().split('T')[0] && value > currentTime) {
        errors[name] = 'Future times are not allowed';
      } else {
        delete errors[name];
      }
    }

    setValidationErrors(errors);

    if (editData.id) {
      setEditData((prv) => ({ ...prv, [name]: value }));
    } else {
      setAddData((prv) => ({ ...prv, [name]: value }));
    }
  };

  const handleInputUnitChange = (selectedOption) => {
    setInputUnit(selectedOption.value);

    if (addData.measurement) {
      let convertedValue = addData.measurement;

      if (inputUnit === "mg/dL" && selectedOption.value === "mmol/L") {
        convertedValue = (parseFloat(addData.measurement) / 18).toFixed(1);
      } else if (inputUnit === "mmol/L" && selectedOption.value === "mg/dL") {
        convertedValue = Math.round(parseFloat(addData.measurement) * 18);
      }

      setAddData(prev => ({ ...prev, measurement: convertedValue }));
    }

    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.measurement;
      return newErrors;
    });
  };

  const handleSelectChange = (e) => {
    if (editData.id) {
      setEditData((prv) => ({ ...prv, measurement_type: e.value }));
    } else {
      setAddData((prv) => ({ ...prv, measurement_type: e.value }));
    }
  };

  const validateForm = (formData) => {
    const errors = {};

    if (!formData.measurement || formData.measurement <= 0) {
      errors.measurement = 'Please enter a valid glucose level';
    } else {
      const glucoseValue = parseFloat(formData.measurement);

      if (inputUnit === 'mg/dL') {
        if (glucoseValue < 20) {
          errors.measurement = 'Glucose must be at least 20 mg/dL';
        } else if (glucoseValue > 2500) {
          errors.measurement = 'Glucose must be less than 2500 mg/dL';
        }
      } else {
        if (glucoseValue < 1) {
          errors.measurement = 'Glucose must be at least 1 mmol/L';
        } else if (glucoseValue > 150) {
          errors.measurement = 'Glucose must be less than 150 mmol/L';
        }
      }
    }

    if (!formData.record_date) {
      errors.record_date = 'Date is required';
    }

    if (!formData.record_time) {
      errors.record_time = 'Time is required';
    }

    if (formData.record_date && new Date(formData.record_date) > new Date()) {
      errors.record_date = 'Future dates are not allowed';
    }

    if (formData.record_date === new Date().toISOString().split('T')[0] && formData.record_time) {
      const currentTime = new Date().toTimeString().slice(0, 5);
      if (formData.record_time > currentTime) {
        errors.record_time = 'Future times are not allowed';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEdit = async (e) => {
    e.preventDefault();

    if (!validateForm(editData)) {
      return;
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      record_id: editData?.id,
      form_data: {
        measurement: editData.measurement,
        measurement_type: editData.measurement_type,
        record_date: editData?.record_date,
        record_time: editData?.record_time,
        notes: editData?.notes,
      },
    };

    setLoadingEdit(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/edit",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        handleModalClose();
        fetchData();
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to update record");
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateForm(addData)) {
      return;
    }

    let measurementValue = addData.measurement;
    if (inputUnit !== glucoseUnit) {
      if (inputUnit === "mg/dL" && glucoseUnit === "mmol/L") {
        measurementValue = (parseFloat(addData.measurement) / 18).toFixed(1);
      } else if (inputUnit === "mmol/L" && glucoseUnit === "mg/dL") {
        measurementValue = Math.round(parseFloat(addData.measurement) * 18);
      }
    }

    const dataToSend = {
      logged_id: patientData?.logged_id,
      form_data: {
        measurement: measurementValue,
        measurement_type: addData.measurement_type,
        record_date: addData.record_date,
        record_time: addData.record_time,
        notes: addData.notes,
      },
    };

    setIsLoading(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/add",
        dataToSend
      );
      if (response?.status === "success") {
        toast.success("Record has been added successfully");
        handleModalClose();
        setAddData({
          measurement: "",
          measurement_type: "Fasting",
          record_date: "",
          record_time: "",
          notes: "",
        });
        setInputUnit("mg/dL");
        fetchData();
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to add record");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const data = {
      logged_id: patientData?.logged_id,
      record_id: deleteId,
    };

    setLoadingDelete(true);
    try {
      const response = await SendPostRequest(
        "healthcare/blood_glucose/delete",
        data
      );
      if (response?.status === "success") {
        toast.success(response?.message);
        fetchData();
        fetchGraphData();
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to delete record");
    } finally {
      setLoadingDelete(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleFilterChange = (e) => {
    const { value, name } = e.target;
    setFilterData((prv) => ({ ...prv, [name]: value }));
  };

  const handleFilter = async (e) => {
    e.preventDefault();
    if (!filterData?.date_from || !filterData?.date_to) {
      toast.error("Please select both from and to dates");
      return;
    }

    if (new Date(filterData.date_from) > new Date(filterData.date_to)) {
      toast.error("From date cannot be after to date");
      return;
    }

    fetchData(filterData);
  };

  const exportToCSV = () => {
    // Calculate statistics
    const glucoseValues = getData.map((item) => parseFloat(item.measurement));
    const maxValue =
      glucoseValues.length > 0 ? Math.max(...glucoseValues).toFixed(1) : "N/A";
    const minValue =
      glucoseValues.length > 0 ? Math.min(...glucoseValues).toFixed(1) : "N/A";
    const avgValue =
      glucoseValues.length > 0
        ? (
            glucoseValues.reduce((a, b) => a + b, 0) / glucoseValues.length
          ).toFixed(1)
        : "N/A";

    const title = "Blood Glucose Records";

    // Date Generated
    const dateGenerated = `Date Generated: ${formatDate(today)}`;
    const emptyLine = "";
    // Table headers
    const headers = [
      "Sr.No",
      "Record Date",
      "Record Time",
      "Glucose Level",
      "Type",
      "Note",
      "Status",
    ];
    const csvData = getData.map((item, index) => [
      index + 1,
      formatDate(item.record_date),
      formatTime(item.record_time),
      item.measurement,
      item.measurement_type,
      item.notes || "--",
      item.status || "--",
    ]);

    // Summary information
    const summary = [
      // ["Date Generated", today],
      ["Total Records", getData.length],
      ["Unit", glucoseUnit],
      ["Measurement Type", selectedMeasurementType.label],
      ["Maximum Value", maxValue],
      ["Minimum Value", minValue],
      ["Average Value", avgValue],
    ];

    // CSV content: table first, then summary
    const csvContent = [
      title,
      dateGenerated,
      emptyLine,
      headers.join(","), // table headers
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(",")), // table rows
      "", // empty line before summary
      "Blood Glucose Records Summary", // summary title
      ...summary.map((row) => row.join(": ")), // summary rows
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `blood_glucose_${today}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    // Calculate statistics
    const glucoseValues = getData.map((item) => parseFloat(item.measurement));
    const maxValue =
      glucoseValues.length > 0 ? Math.max(...glucoseValues).toFixed(1) : "N/A";
    const minValue =
      glucoseValues.length > 0 ? Math.min(...glucoseValues).toFixed(1) : "N/A";
    const avgValue =
      glucoseValues.length > 0
        ? (
            glucoseValues.reduce((a, b) => a + b, 0) / glucoseValues.length
          ).toFixed(1)
        : "N/A";

    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text("Blood Glucose Records", 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${formatDate(today)}`, 14, 30);

    // Table
    const tableYStart = 40;
    doc.autoTable({
      startY: tableYStart,
      head: [
        [
          "Sr.No",
          "Date",
          "Time",
          `Glucose (${glucoseUnit})`,
          "Type",
          "Notes",
          "Status",
        ],
      ],
      body: getData.map((item, index) => [
        index + 1,
        formatDate(item.record_date),
        formatTime(item.record_time),
        item.measurement,
        item.measurement_type,
        item.notes || "--",
        item.status || "--",
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [66, 139, 202],
      },
      columnStyles: {
        0: { cellWidth: 12 }, // Sr.No
        1: { cellWidth: 25 }, // Date
        2: { cellWidth: 20 }, // Time
        3: { cellWidth: 30 }, // Glucose
        4: { cellWidth: 25 }, // Type
        5: { cellWidth: 60 }, // Notes
        6: { cellWidth: 20 }, // Status
      },
      styles: { fontSize: 10 },
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    // doc.setFontSize(11);
    // doc.text(`Date Generated: ${today}`, 14, finalY);
    finalY += 8;
    doc.text(`Total Records: ${getData.length}`, 14, finalY);
    finalY += 8;
    doc.text(`Unit: ${glucoseUnit}`, 14, finalY);
    finalY += 8;
    doc.text(`Measurement Type: ${selectedMeasurementType.label}`, 14, finalY);
    finalY += 8;
    doc.text(`Maximum Value: ${maxValue} ${glucoseUnit}`, 14, finalY);
    finalY += 8;
    doc.text(`Minimum Value: ${minValue} ${glucoseUnit}`, 14, finalY);
    finalY += 8;
    doc.text(`Average Value: ${avgValue} ${glucoseUnit}`, 14, finalY);

    doc.save(`blood_glucose_records_${today}.pdf`);
  };

  const getGlucoseStatus = (glucose, type) => {
    let status;
    const glucoseValue = parseFloat(glucose);

    if (glucoseUnit === "mg/dL") {
      if (type === "Fasting") {
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 100) status = "normal";
        else if (glucoseValue > 100 && glucoseValue <= 125) status = "elevated";
        else status = "high";
      } else if (type === "After Meal") {
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 140) status = "normal";
        else if (glucoseValue > 140 && glucoseValue <= 200) status = "elevated";
        else status = "high";
      } else {
        if (glucoseValue < 70) status = "low";
        else if (glucoseValue >= 70 && glucoseValue <= 140) status = "normal";
        else if (glucoseValue > 140 && glucoseValue <= 200) status = "elevated";
        else status = "high";
      }
    } else {
      if (type === "Fasting") {
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 5.6) status = "normal";
        else if (glucoseValue > 5.6 && glucoseValue <= 6.9) status = "elevated";
        else status = "high";
      } else if (type === "After Meal") {
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 7.8) status = "normal";
        else if (glucoseValue > 7.8 && glucoseValue <= 11.1)
          status = "elevated";
        else status = "high";
      } else {
        if (glucoseValue < 3.9) status = "low";
        else if (glucoseValue >= 3.9 && glucoseValue <= 7.8) status = "normal";
        else if (glucoseValue > 7.8 && glucoseValue <= 11.1)
          status = "elevated";
        else status = "high";
      }
    }

    return status;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "low":
        return "info";
      case "normal":
        return "success";
      case "elevated":
        return "warning";
      case "high":
        return "danger";
      default:
        return "secondary";
    }
  };
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="blood-glucose-container Prescripition">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h3>Blood Glucose</h3>
        <div
          className="d-flex gap-2"
          style={{
            justifyContent: isMobile ? "space-between" : "",
            width: isMobile ? "100%" : "",
          }}
        >
          <button
            className="btn btn-default d-flex align-items-center"
            onClick={handleModalOpen}
            style={{ alignItems: "center" }}
          >
            <MdAdd className="me-1" /> Add Record
          </button>
          <div>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToCSV}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> CSV
            </button>
            <button
              className="btn btn-default align-items-center"
              onClick={exportToPDF}
              style={{ alignItems: "center" }}
              disabled={getData.length === 0}
            >
              <MdFileDownload className="me-1" /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`chart-views-btn ${
                viewMode === "chart"
                  ? "chart-active-views-btn"
                  : "chart-views-btn"
              }`}
              onClick={() => setViewMode("chart")}
            >
              Chart View
            </button>
            <button
              type="button"
              className={`chart-views-btn-second ${
                viewMode === "table"
                  ? "chart-active-views-btn"
                  : "chart-views-btn-second"
              }`}
              onClick={() => setViewMode("table")}
            >
              Table View
            </button>
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`chart-views-btn ${
                  glucoseUnit === "mg/dL"
                    ? "chart-active-views-btn"
                    : "btn-outline-primary"
                }`}
                onClick={() => setGlucoseUnit("mg/dL")}
              >
                mg/dL
              </button>
              <button
                type="button"
                className={`chart-views-btn-second ${
                  glucoseUnit === "mmol/L"
                    ? "chart-active-views-btn"
                    : "chart-views-btn-second"
                }`}
                onClick={() => setGlucoseUnit("mmol/L")}
              >
                mmol/L
              </button>
            </div>
          </div>
        </div>

        <div>
          {viewMode === "chart" && (
            <div className="form-group d-flex align-items-center gap-2 flex-wrap">
              <div className="ms-2">
                <label>Time Range:</label>
                <Select
                  className="time-range-select"
                  value={selectedTimeStamp}
                  options={timeStamp}
                  onChange={setSelectedTimeStamp}
                  isSearchable={false}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: "120px",
                    }),
                  }}
                />
              </div>
              <div
                className="ms-2"
                style={{
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "start" : "center",
                }}
              >
                <label className="me-2">Measurement Type:</label>
                <Select
                  className="measurement-type-select"
                  value={selectedMeasurementType}
                  options={measurementTypeOptions}
                  onChange={setSelectedMeasurementType}
                  isSearchable={false}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: "140px",
                    }),
                  }}
                />
              </div>

              <div
                className="ms-2"
                style={{
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "start" : "center",
                }}
              >
                <label>Time Range:</label>
                <Select
                  className="time-range-select"
                  value={selectedTimeStamp}
                  options={timeStamp}
                  onChange={setSelectedTimeStamp}
                  isSearchable={false}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: "120px",
                    }),
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {viewMode === "chart" ? (
          <BloodGlucoseChart
            data={graphData}
            loading={loadingGraph}
            unit={glucoseUnit}
            measurementType={selectedMeasurementType.value}
          />
        ) : (
          <>
            {/* <div className="d-flex justify-content-between align-items-center mb-3"> */}
            <div className="d-flex align-items-center justify-content-between gap-2">
              <form
                onSubmit={handleFilter}
                className="d-flex gap-2 align-items-center justify-content-between flex-wrap"
              >
                <div>
                  <label htmlFor="from" className="form-label mb-0">
                    From:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_from"
                    className="form-control"
                    value={filterData?.date_from || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>
                <div>
                  <label htmlFor="to" className="form-label mb-0">
                    To:
                  </label>
                  <input
                    required
                    type="date"
                    name="date_to"
                    className="form-control"
                    value={filterData?.date_to || ""}
                    onChange={handleFilterChange}
                    max={today}
                    style={{ width: "140px" }}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-default"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Filter
                </button>
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    setFilterData(null);
                    fetchData(null);
                  }}
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  Clear
                </button>

                <div
                  className="d-flex align-items-center ms-2"
                  style={{ marginTop: isMobile ? "0rem" : "1.5rem" }}
                >
                  <MdSort className="me-1" />
                  <Select
                    className="sort-select"
                    value={sortOption}
                    options={sortOptions}
                    onChange={setSortOption}
                    isSearchable={false}
                    placeholder="Sort by..."
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minWidth: "180px",
                      }),
                    }}
                  />
                </div>
              </form>
              <div className="d-flex align-items-end ms-auto mt-2 mt-md-0">
                <div className="me-2">
                  <label className="form-label mb-0">Type:</label>
                  <Select
                    className="filter-dropdown-blood-glucose-list"
                    value={selectedMeasurementType}
                    options={measurementTypeOptions}
                    onChange={setSelectedMeasurementType}
                    isSearchable={false}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minWidth: "160px",
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
            {/* </div> */}

            {isLoading ? (
              <div className="text-center py-4">
                <Spinner />
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mt-2">
                  <thead className="thead-light">
                    <tr>
                      <th
                        scope="col"
                        style={{ width: "10%", textAlign: "center" }}
                      >
                        SR.No
                      </th>
                      <th scope="col">Record Date</th>
                      <th scope="col">Record Time</th>
                      <th scope="col">Glucose Level ({glucoseUnit})</th>
                      <th scope="col">Type</th>
                      {/* <th scope="col" style={{ width: '180px' }}>Note</th> */}
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getData.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No blood glucose records found
                        </td>
                      </tr>
                    ) : (
                      getData.map((item, index) => {
                        const statusColor = item?.color || "#000";

                        return (
                          <tr key={item.id || index}>
                            <td style={{ textAlign: "center" }}>{index + 1}</td>
                            <td>{formatDate(item.record_date)}</td>
                            <td>{formatTime(item.record_time)}</td>
                            <td>{item.measurement}</td>
                            <td>{item.measurement_type}</td>
                            {/* <td style={{ width: '180px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                              {item.notes || "-"}
                            </td> */}
                            <td>
                              <span
                                className={`badge`}
                                style={{
                                  backgroundColor: statusColor,
                                  color: "#fff",
                                  textTransform: "capitalize",
                                  fontSize: "10px",
                                }}
                              >
                                {item.status || "Unknown"}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-default"
                                  onClick={() => {
                                    setEditData(item);
                                    handleModalOpen();
                                  }}
                                  title="Edit"
                                >
                                  <MdEdit />
                                </button>
                                <button
                                  className="btn btn-default"
                                  onClick={() => {
                                    setDeleteId(item.id);
                                    setShowDeleteConfirm(true);
                                  }}
                                  disabled={LoadingDelete}
                                  title="Delete"
                                >
                                  {LoadingDelete && deleteId === item.id ? (
                                    <SpinnerBoot size="sm" />
                                  ) : (
                                    <MdDelete />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editData.id
              ? "Edit Blood Glucose Record"
              : "Add Blood Glucose Record"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={editData.id ? handleEdit : handleAdd}>
          <Modal.Body>
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Glucose Level*</label>
                  <div className="input-group">
                    <input
                      type="number"
                      step={inputUnit === "mmol/L" ? "0.1" : "1"}
                      className={`form-control ${
                        validationErrors.measurement ? "is-invalid" : ""
                      }`}
                      placeholder={inputUnit === "mg/dL" ? "20-2500" : "1-150"}
                      value={
                        editData.id ? editData.measurement : addData.measurement
                      }
                      name="measurement"
                      onChange={handleOnChange}
                      onKeyPress={(e) => {
                        // prevent '-' and 'e' always
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                        // additionally prevent '.' if unit is mg/dL
                        if (inputUnit === "mg/dL" && e.key === ".") {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      max={inputUnit === "mg/dL" ? "2500" : "150"}
                      required
                    />
                    <div className="input-group-append">
                      <Select
                        options={unitOptions}
                        value={unitOptions.find(
                          (opt) => opt.value === inputUnit
                        )}
                        onChange={handleInputUnitChange}
                        isSearchable={false}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minWidth: "100px",
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            height: "100%",
                          }),
                        }}
                      />
                    </div>
                    {validationErrors.measurement && (
                      <div className="invalid-feedback d-block mt-0">
                        {validationErrors.measurement}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Measurement Type*</label>
                  <Select
                    className="measurement-type-select glucose-add-record-type-select"
                    options={measurementTypeOptions.filter(
                      (opt) => opt.value !== "All"
                    )}
                    value={{
                      value: editData.id
                        ? editData.measurement_type
                        : addData.measurement_type,
                      label: editData.id
                        ? editData.measurement_type
                        : addData.measurement_type,
                    }}
                    onChange={handleSelectChange}
                    isSearchable={false}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Date*</label>
                  <input
                    style={{ height: "50px" }}
                    type="date"
                    className={`form-control ${
                      validationErrors.record_date ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_date : addData.record_date
                    }
                    name="record_date"
                    onChange={handleOnChange}
                    max={today}
                    required
                  />
                  {validationErrors.record_date && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.record_date}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Time*</label>
                  <input
                    style={{ height: "50px" }}
                    type="time"
                    className={`form-control mb-0 ${
                      validationErrors.record_time ? "is-invalid" : ""
                    }`}
                    value={
                      editData.id ? editData.record_time : addData.record_time
                    }
                    name="record_time"
                    onChange={handleOnChange}
                    required
                  />
                  {validationErrors.record_time && (
                    <div className="invalid-feedback d-block mt-0">
                      {validationErrors.record_time}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    placeholder="Add any notes about this measurement"
                    value={editData.id ? editData.notes : addData.notes}
                    name="notes"
                    onChange={handleOnChange}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              variant="btn btn-default"
              type="submit"
              disabled={LoadingEdit || isLoading}
            >
              {LoadingEdit || isLoading ? "Saving..." : "Save Record"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this blood glucose record? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn btn-default"
            variant="btn btn-default"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-default"
            variant="danger"
            onClick={handleDelete}
            disabled={LoadingDelete}
          >
            {LoadingDelete ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BloodGlucose;